import { useEffect, useMemo, useRef, useState } from 'react'
import { MessageSquareDashed, RefreshCw, Wifi, WifiOff } from 'lucide-react'
import { toast } from 'sonner'

import { WsStatusBadge } from '@/components/WsStatusBadge'
import { ContactDetailsModal } from '@/components/chat/ContactDetailsModal'
import { ConversationList } from '@/components/chat/ConversationList'
import { ChatWindow } from '@/components/chat/ChatWindow'
import { Button } from '@/components/common/Button'
import { Card } from '@/components/common/Card'
import { PageSpinner } from '@/components/common/Spinner'
import { useAuth } from '@/hooks/useAuth'
import { useConversationSync } from '@/hooks/useConversationSync'
import { useWebSocket } from '@/hooks/useWebSocket'
import ConversationAIService from '@/services/conversation-ai.service'
import MessagesService from '@/services/messages.service'
import { useInstancesStore } from '@/stores/instancesStore'
import { getStoredActiveChat, useMessagesStore } from '@/stores/messagesStore'
import type { Contact, Conversation, ConversationAISetting } from '@/types'

const ALL_INSTANCES_VALUE = '__all__'

export default function ChatPage() {
  const { can } = useAuth()
  const {
    instances,
    selectedInstance,
    isLoading: isLoadingInstances,
    fetchInstances,
    setSelected,
  } = useInstancesStore()
  const {
    conversations,
    activeConversation,
    isLoadingConversations,
    fetchConversations,
    fetchWorkspaceConversations,
    fetchMessages,
    setActiveConversation,
    clearActiveConversation,
  } = useMessagesStore()

  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [viewAllInstances, setViewAllInstances] = useState(true)
  const [isFinalizing, setIsFinalizing] = useState(false)
  const [contactDetailsConversation, setContactDetailsConversation] = useState<Conversation | null>(null)
  const [conversationAiSettings, setConversationAiSettings] = useState<Record<string, ConversationAISetting>>({})

  const wsInstanceId = viewAllInstances ? ALL_INSTANCES_VALUE : selectedInstance?.id
  const ws = useWebSocket(wsInstanceId)
  const prevWsConnected = useRef(false)

  const selectedInstanceRef = useRef(selectedInstance)
  selectedInstanceRef.current = selectedInstance
  const activeConversationRef = useRef(activeConversation)
  activeConversationRef.current = activeConversation
  const searchRef = useRef(search)
  searchRef.current = search
  const statusFilterRef = useRef(statusFilter)
  statusFilterRef.current = statusFilter
  const viewAllInstancesRef = useRef(viewAllInstances)
  viewAllInstancesRef.current = viewAllInstances
  const connectedInstances = useMemo(
    () => instances.filter((instance) => instance.status === 'connected'),
    [instances],
  )

  const instanceLabelById = useMemo(() => {
    const map = new Map<string, string>()
    for (const instance of instances) {
      map.set(instance.id, instance.name)
    }
    return map
  }, [instances])
  const replaceMessages = useMessagesStore((state) => state.replaceMessages)
  const { status: conversationSyncStatus, isReconciling } = useConversationSync({
    conversationId: activeConversation?.id,
    enabled: Boolean(activeConversation),
    wsConnected: ws.connected,
    onMessages: replaceMessages,
  })

  useEffect(() => {
    fetchInstances().catch(() => undefined)
  }, [fetchInstances])

  useEffect(() => {
    if (!selectedInstance && connectedInstances.length > 0) {
      const storedActiveChat = getStoredActiveChat()
      const storedInstance = connectedInstances.find(
        (instance) => instance.id === storedActiveChat?.instance_id,
      )
      setSelected(storedInstance ?? connectedInstances[0])
    }
  }, [connectedInstances, selectedInstance, setSelected])

  useEffect(() => {
    if (viewAllInstances) {
      fetchWorkspaceConversations({
        search: search || undefined,
        status: statusFilter || undefined,
      }).catch(() => undefined)
      return
    }
    if (!selectedInstance) return
    fetchConversations(selectedInstance.id, {
      search: search || undefined,
      status: statusFilter || undefined,
    }).catch(() => undefined)
  }, [
    fetchConversations,
    fetchWorkspaceConversations,
    search,
    selectedInstance,
    statusFilter,
    viewAllInstances,
  ])

  useEffect(() => {
    clearActiveConversation({ preserveStorage: true })
  }, [clearActiveConversation, selectedInstance?.id, viewAllInstances])

  useEffect(() => {
    const wasConnected = prevWsConnected.current
    prevWsConnected.current = ws.connected
    if (!ws.connected || wasConnected) return

    if (viewAllInstancesRef.current) {
      fetchWorkspaceConversations({
        search: searchRef.current || undefined,
        status: statusFilterRef.current || undefined,
      }).catch(() => undefined)
    } else {
      const instance = selectedInstanceRef.current
      if (!instance) return
      fetchConversations(instance.id, {
        search: searchRef.current || undefined,
        status: statusFilterRef.current || undefined,
      }).catch(() => undefined)
    }

    const conversation = activeConversationRef.current
    if (conversation) {
      fetchMessages(conversation.instance_id, conversation.contact_id).catch(() => undefined)
    }
  }, [ws.connected, fetchConversations, fetchWorkspaceConversations, fetchMessages])

  useEffect(() => {
    if (!viewAllInstances && !selectedInstance) return
    if (conversations.length === 0) {
      clearActiveConversation()
      return
    }

    const stillExists = conversations.some((conversation) => conversation.id === activeConversation?.id)
    if (stillExists) return

    const storedActiveChat = getStoredActiveChat()
    const matchesStored = (conversation: Conversation) =>
      conversation.id === storedActiveChat?.conversation_id ||
      conversation.contact_id === storedActiveChat?.contact_id

    const nextConversation = viewAllInstances
      ? conversations.find(matchesStored) ?? conversations[0]
      : storedActiveChat?.instance_id === selectedInstance?.id
        ? conversations.find(matchesStored) ?? conversations[0]
        : conversations[0]

    setActiveConversation(nextConversation)
    fetchMessages(nextConversation.instance_id, nextConversation.contact_id).catch(() => undefined)
  }, [
    activeConversation?.id,
    clearActiveConversation,
    conversations,
    fetchMessages,
    selectedInstance,
    setActiveConversation,
    viewAllInstances,
  ])

  useEffect(() => {
    if (viewAllInstances) return
    if (!selectedInstance) return
    if (selectedInstance.status === 'connected') return

    const nextConnected = connectedInstances[0]
    if (nextConnected) {
      setSelected(nextConnected)
    }
  }, [connectedInstances, selectedInstance, setSelected, viewAllInstances])

  useEffect(() => {
    if (!activeConversation) return
    let cancelled = false

    ConversationAIService.get(activeConversation.id)
      .then((setting) => {
        if (cancelled) return
        setConversationAiSettings((current) => ({ ...current, [activeConversation.id]: setting }))
      })
      .catch(() => undefined)

    return () => {
      cancelled = true
    }
  }, [activeConversation])

  async function handleRefresh() {
    if (viewAllInstances) {
      await fetchWorkspaceConversations({
        search: search || undefined,
        status: statusFilter || undefined,
      })
    } else {
      if (!selectedInstance) return
      await fetchConversations(selectedInstance.id, {
        search: search || undefined,
        status: statusFilter || undefined,
      })
    }
    if (activeConversation) {
      await fetchMessages(activeConversation.instance_id, activeConversation.contact_id)
    }
  }

  async function handleConversationSelect(conversation: Conversation) {
    setActiveConversation(conversation)
    await fetchMessages(conversation.instance_id, conversation.contact_id)
  }

  function handleOpenContactDetails(conversation: Conversation) {
    setContactDetailsConversation(conversation)
  }

  function handleContactDetailsSaved(contact: Contact, aiSetting: ConversationAISetting) {
    setConversationAiSettings((current) => ({ ...current, [aiSetting.conversation_id]: aiSetting }))

    const store = useMessagesStore.getState()
    const targetConversation = store.conversations.find((item) => item.id === aiSetting.conversation_id)
    if (!targetConversation) return
    store.upsertConversation({
      ...targetConversation,
      contact,
    })
  }

  async function handleFinalizeConversation() {
    if (!activeConversation || activeConversation.status === 'closed') return

    setIsFinalizing(true)
    try {
      const updated = await MessagesService.closeConversation(
        activeConversation.instance_id,
        activeConversation.id,
      )
      useMessagesStore.getState().removeConversation(updated.id)
      clearActiveConversation()
      toast.success('Conversa finalizada.')
    } catch {
      toast.error('Não foi possível finalizar a conversa.')
    } finally {
      setIsFinalizing(false)
    }
  }

  if (isLoadingInstances && instances.length === 0) {
    return <PageSpinner label="Carregando ambiente de atendimento..." />
  }

  if (connectedInstances.length === 0) {
    return (
      <Card className="p-8">
        <h2>Nenhuma instância conectada</h2>
        <p>Conecte uma instância para começar o atendimento em tempo real.</p>
      </Card>
    )
  }

  if (!viewAllInstances && !selectedInstance) {
    return <PageSpinner label="Preparando instância conectada..." />
  }


  return (
    <div className="flex min-h-0 flex-1 flex-col space-y-5">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
        <div>
          <p className="text-sm text-[var(--text-secondary)]">
            {viewAllInstances
              ? 'Atendimento consolidado de todas as instâncias do workspace.'
              : 'Atendimento em tempo real da instância ativa.'}
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <WsStatusBadge
              state={ws.connectionState}
              attempt={ws.reconnectAttempt}
              onManualReconnect={ws.reconnect}
            />
            <span className="text-xs text-[var(--text-muted)]">
              {conversations.length} conversa{conversations.length === 1 ? '' : 's'}
            </span>
          </div>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <select
            value={viewAllInstances ? ALL_INSTANCES_VALUE : selectedInstance?.id ?? ''}
            onChange={(event) => {
              const value = event.target.value
              if (value === ALL_INSTANCES_VALUE) {
                setViewAllInstances(true)
                return
              }
              const next = connectedInstances.find((instance) => instance.id === value)
              if (next) {
                setViewAllInstances(false)
                setSelected(next)
              }
            }}
            className="appearance-none rounded-[14px] border border-[var(--border-medium)] bg-[var(--glass-light)] px-4 py-2.5 text-sm font-medium text-[var(--text-primary)] outline-none transition hover:border-[var(--color-brand)] focus:border-[var(--color-brand)] focus:ring-2 focus:ring-[rgba(var(--color-brand-rgb),0.18)]"
          >
            <option value={ALL_INSTANCES_VALUE}>Todas as instâncias</option>
            {connectedInstances.map((instance) => (
              <option key={instance.id} value={instance.id}>
                {instance.name}
              </option>
            ))}
          </select>

          <Button
            variant="outline"
            icon={<RefreshCw size={14} />}
            onClick={() => handleRefresh().catch(() => undefined)}
          >
            Atualizar
          </Button>
        </div>
      </div>

      <div className="glass-card flex min-h-0 flex-1 overflow-hidden">
        <div
          className={
            activeConversation
              ? 'hidden md:flex glass-sidebar min-h-0 md:w-[320px] lg:w-[340px] xl:w-[360px] shrink-0 flex-col'
              : 'glass-sidebar min-h-0 w-full md:w-[320px] lg:w-[340px] xl:w-[360px] shrink-0 flex flex-col'
          }
        >
          <ConversationList
            conversations={conversations}
            activeConversation={activeConversation}
            isLoading={isLoadingConversations}
            onSelect={handleConversationSelect}
            onOpenContactDetails={handleOpenContactDetails}
            onSearch={setSearch}
            onFilterStatus={setStatusFilter}
            instanceLabelById={viewAllInstances ? instanceLabelById : undefined}
          />
        </div>

        <div
          className={activeConversation ? 'flex min-w-0 flex-1 min-h-0' : 'hidden min-w-0 flex-1 min-h-0 md:flex'}
        >
          {activeConversation ? (
            <ChatWindow
              conversation={activeConversation}
              ws={ws}
              onClose={clearActiveConversation}
              onFinalize={handleFinalizeConversation}
              onOpenContactDetails={() => handleOpenContactDetails(activeConversation)}
              canFinalize={can('finalize_conversations')}
              isFinalizing={isFinalizing}
              aiEnabled={conversationAiSettings[activeConversation.id]?.ai_enabled ?? null}
              syncStatus={conversationSyncStatus}
              isReconciling={isReconciling}
            />
          ) : (
            <div className="flex h-full min-h-0 w-full items-center justify-center p-6">
              <Card className="p-8 max-w-md">
                <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-[var(--color-brand-light)] text-[var(--color-brand)]">
                  <MessageSquareDashed size={22} />
                </div>
                <h2>Selecione uma conversa</h2>
                <p>
                  {connectedInstances.length > 0
                    ? 'Escolha uma conversa ao lado para visualizar o historico e responder em tempo real.'
                    : 'Conecte uma instancia para comecar a receber conversas.'}
                </p>
                <div className="mt-4 flex flex-wrap items-center gap-2 text-sm text-[var(--text-muted)]">
                  {ws.connected ? <Wifi size={14} /> : <WifiOff size={14} />}
                  <span>
                    {ws.connected
                      ? 'Canal de eventos ativo para novas mensagens.'
                      : ws.connectionState === 'CIRCUIT_OPEN'
                        ? 'A reconexao automatica foi suspensa. Use o botao para tentar novamente.'
                        : 'O chat esta tentando restabelecer a conexao em segundo plano.'}
                  </span>
                </div>
              </Card>
            </div>
          )}
        </div>
      </div>

      <ContactDetailsModal
        open={Boolean(contactDetailsConversation)}
        conversation={contactDetailsConversation}
        instanceName={
          contactDetailsConversation
            ? instanceLabelById.get(contactDetailsConversation.instance_id)
            : undefined
        }
        onClose={() => setContactDetailsConversation(null)}
        onSaved={handleContactDetailsSaved}
      />
    </div>
  )
}
